import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import type { Copy } from './copy';

/*
 * The import, played as a loop over the sources Chefty reads: a TikTok, an Instagram
 * reel, a YouTube Short and a recipe site in turn (apps/worker/src/ai/normalizers).
 * Each round lights its source in the strip, pastes the link, taps Import, runs the
 * app's real stages (Sending, Reading, Done) while the ingredients land one by one,
 * and ends on the draft with the dog's line from the review screen. It only runs while
 * on screen; with reduced motion it shows the first finished draft and stands still.
 *
 * Source marks are neutral glyphs with the name beside them, not the platforms' logos.
 */

const PHASES = ['empty', 'pasted', 'tapped', 'send', 'read', 'done'] as const;
type Phase = (typeof PHASES)[number];

/** How long each phase holds, in ms. "read" is long enough for every ingredient to land. */
const HOLD: Record<Phase, number> = {
  empty: 1000,
  pasted: 900,
  tapped: 500,
  send: 900,
  read: 2300,
  done: 3200,
};

const PROGRESS: Record<Phase, string> = {
  empty: '0%',
  pasted: '0%',
  tapped: '0%',
  send: '28%',
  read: '86%',
  done: '100%',
};

const at = (phase: Phase, from: Phase) => PHASES.indexOf(phase) >= PHASES.indexOf(from);

type SourceId = keyof Copy['importDemo']['sources'];

const GLYPHS: Record<SourceId, ReactNode> = {
  // a short-video frame with a note
  tiktok: (
    <>
      <rect x="5" y="3" width="14" height="18" rx="3" />
      <path d="M13.5 8v6.2a2 2 0 11-2-2M13.5 8c.4 1.3 1.4 2.2 2.7 2.4" />
    </>
  ),
  // a rounded camera square
  instagram: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="5" />
      <circle cx="12" cy="12" r="3.6" />
      <circle cx="16.6" cy="7.4" r=".6" />
    </>
  ),
  // a play button in a wide frame
  youtube: (
    <>
      <rect x="3" y="6" width="18" height="12" rx="4" />
      <path d="M10.5 9.5v5l4-2.5z" />
    </>
  ),
  web: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M3.5 12h17M12 3.5c2.4 2.3 3.6 5.1 3.6 8.5s-1.2 6.2-3.6 8.5c-2.4-2.3-3.6-5.1-3.6-8.5s1.2-6.2 3.6-8.5z" />
    </>
  ),
  photo: (
    <>
      <path d="M4 8h3l2-3h6l2 3h3v11H4z" />
      <circle cx="12" cy="13" r="3.5" />
    </>
  ),
  text: (
    <>
      <path d="M7 3h7l4 4v14H7z" />
      <path d="M14 3v4h4M10 12h5M10 16h5" />
    </>
  ),
};
const SOURCE_ORDER: SourceId[] = ['tiktok', 'instagram', 'youtube', 'web', 'photo', 'text'];

export const ImportDemo = ({ c }: { c: Copy }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [still] = useState(() => window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const [phase, setPhase] = useState<Phase>(still ? 'done' : 'empty');
  const [round, setRound] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || still) return;
    const io = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), {
      threshold: 0.35,
    });
    io.observe(el);
    return () => io.disconnect();
  }, [still]);

  useEffect(() => {
    if (!visible) return;
    const timer = window.setTimeout(() => {
      const next = PHASES[(PHASES.indexOf(phase) + 1) % PHASES.length];
      if (next === 'empty') setRound((r) => r + 1);
      setPhase(next);
    }, HOLD[phase]);
    return () => window.clearTimeout(timer);
  }, [phase, visible]);

  const d = c.importDemo;
  const demo = d.demos[round % d.demos.length];
  const source = demo.source as SourceId;
  const pasted = at(phase, 'pasted');
  const started = at(phase, 'send');
  const line = !started
    ? d.lines.usually
    : phase === 'send'
      ? d.lines.send
      : phase === 'read'
        ? d.lines.read
        : d.lines.done;
  const stageOn = (k: number) =>
    (k === 0 && started) || (k === 1 && at(phase, 'read')) || (k === 2 && phase === 'done');

  return (
    <div className="ui-card imp" ref={ref} data-phase={phase} role="img" aria-label={d.label}>
      <ul className="sources" aria-hidden="true">
        {SOURCE_ORDER.map((id) => (
          <li key={id} className={id === source ? 'on' : undefined}>
            <svg className="i" viewBox="0 0 24 24">
              {GLYPHS[id]}
            </svg>
            {d.sources[id]}
          </li>
        ))}
      </ul>

      <div className={`field${pasted ? ' filled' : ''}`} aria-hidden="true">
        <span className="url" key={`${round}-${pasted}`}>
          {pasted ? demo.link : d.placeholder}
          {!pasted && <i className="caret" />}
        </span>
        <span className={`p${phase === 'tapped' ? ' pressed' : ''}`}>
          {at(phase, 'tapped') ? d.import : d.paste}
          {(phase === 'pasted' || phase === 'tapped') && <i key={phase} className="tap" />}
        </span>
      </div>

      <div className="stages" aria-hidden="true">
        {d.stages.map((stage, k) => (
          <span key={stage} className={stageOn(k) ? 'on' : undefined}>
            {stage}
          </span>
        ))}
      </div>
      <div className="track" aria-hidden="true">
        <i style={{ width: PROGRESS[phase] }} />
      </div>
      <p className="usually" aria-hidden="true">
        {line}
      </p>

      <div className={`draft${at(phase, 'read') ? ' show' : ''}`} aria-hidden="true">
        <div className="draft-head">
          <img src="/landing/dog-review.webp" width={96} height={96} alt="" />
          <div>
            <b>{phase === 'done' ? demo.title : <i className="skel" />}</b>
            <span>{phase === 'done' ? c.shakshuka.hello : demo.link}</span>
          </div>
          <span className="via">
            <svg className="i" viewBox="0 0 24 24">
              {GLYPHS[source]}
            </svg>
            {d.sources[source]}
          </span>
        </div>
        <div className="draft-items" key={round}>
          {demo.items.map(([qty, name], k) => (
            <span key={name} className="ing" style={{ '--k': k } as CSSProperties}>
              {qty && <b>{qty}</b>} {name}
            </span>
          ))}
        </div>
        <div className="draft-foot">
          <span>{c.shakshuka.summary(demo.items.length, demo.steps)}</span>
          <span className="save">{c.shakshuka.save}</span>
        </div>
      </div>
    </div>
  );
};
